import React from 'react';
import type { VehicleDetailsEntity } from '@packages/domain/techline';

import { Button, Typography } from '@packages/ui/shared';
import { getDisplayValue } from '@web/utils';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';

const useStyles = createUseStyles({
    container: {
        backgroundColor: colors.grayOne,
        borderLeft: `solid 4px ${colors.blueFour}`,
    },
});

const possibleVehicleDetailFields = (vehicle: VehicleDetailsEntity) => [
    {
        label: 'techline:vehicleInfo.possibleVehicles.card.transmissionNo',
        value: vehicle?.vehicleTransmissionNumber,
    },
    {
        label: 'techline:vehicleInfo.possibleVehicles.card.engineNo',
        value: vehicle?.engineId,
    },
    {
        label: 'techline:vehicleInfo.possibleVehicles.card.color',
        value: vehicle?.colorDescription,
    },
    {
        label: 'techline:vehicleInfo.possibleVehicles.card.trim',
        value: vehicle?.trimDescription,
    },
    {
        label: 'techline:vehicleInfo.possibleVehicles.card.model',
        value: vehicle?.modelNumber,
    },
    {
        label: 'techline:vehicleInfo.possibleVehicles.card.modelOption',
        value: vehicle?.optionCode,
    },
];

interface VehicleCardProps {
    vehicle: VehicleDetailsEntity;
    onSelect?: () => void;
}

export function VehicleCard({ vehicle, onSelect }: VehicleCardProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const possibleVehicle = possibleVehicleDetailFields(vehicle);
    return (
        <div className={`${classes.container} flex-column p-4`}>
            <div className="pb-2">
                <div>
                    <Typography variant="caption3" color="blueOne">
                        {vehicle.modelYear} {vehicle.model}
                    </Typography>
                </div>
                <div>
                    <Typography variant="caption3" color="blueOne">
                        {t('techline:vehicleInfo.possibleVehicles.card.vin')}: {vehicle.vin}
                    </Typography>
                </div>
            </div>
            <div>
                {possibleVehicle.map(({ label, value }, index) => (
                    <div key={`${label}-${index}`} data-testid={`vehicle-card-${index}`} className="row">
                        <div className="col-5 d-flex">
                            <div>
                                <Typography color="textDarkSecondary">{t(label)}: </Typography>
                            </div>
                        </div>
                        <div className="col-7 d-flex">
                            <div>
                                <Typography>{getDisplayValue(value)}</Typography>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="col-4 d-flex pt-4">
                <Button onPress={onSelect}>{t('techline:vehicleInfo.possibleVehicles.card.button')}</Button>
            </div>
        </div>
    );
}
